import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ExpandCollapseToggle } from "@/components/shared/ExpandCollapseToggle";
import { SearchFilterBar, type FilterColumn } from "@/components/shared/SearchFilterBar";
import { ConfirmDeleteDialog } from "@/components/shared/ConfirmDeleteDialog";
import { ChevronDown, ChevronUp, Info, Loader2, Pencil, Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useGetChecks,
  useUpdateCheck,
  useUpdateFeatureCheckConfig,
  useDeleteFeatureCheckConfig,
} from "@/resources/validator-registry";
import { useGetAiPrompts, useCreateAiPromptVersion } from "@/resources/ai-prompt";
import type { RegistryCheck, RegistryFeatureConfig } from "@/types/validator-registry";
import { EditableText } from "./EditableText";
import { AddCheckToPromptModal } from "./AddCheckToPromptModal";
import { SEVERITY_LABELS, resolveFeatureCategoryKey, resolveFeatureCategoryLabel } from "./constants";

type Severity = "hard_fail" | "warning";

/** Inline segmented Hard Fail / Warning selector for one scoped prompt's edit session. */
function SeverityToggle({
  value,
  onChange,
  promptLabel,
}: {
  value: Severity;
  onChange: (s: Severity) => void;
  promptLabel: string;
}) {
  const options: Severity[] = ["hard_fail", "warning"];
  return (
    <div
      className="inline-flex items-center gap-0.5 rounded-lg bg-muted/50 p-0.5"
      role="group"
      aria-label={`Severity for ${promptLabel}`}
    >
      {options.map((option) => {
        const isActive = option === value;
        return (
          <button
            key={option}
            type="button"
            aria-pressed={isActive}
            onClick={() => onChange(option)}
            className={cn(
              "px-2.5 py-1 rounded-md text-sm font-medium transition-colors",
              isActive
                ? option === "hard_fail"
                  ? "bg-status-error-soft text-foreground"
                  : "bg-status-warning-soft text-foreground"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {SEVERITY_LABELS[option]}
          </button>
        );
      })}
    </div>
  );
}

function Token({ children }: { children: ReactNode }) {
  return <span className="rounded bg-muted px-1.5 py-0.5 text-sm text-foreground">{children}</span>;
}

/** Flat reference copy — no card, so it reads as documentation, not a control. */
function DefinitionBlock({ children }: { children: ReactNode }) {
  return <div className="space-y-3 text-sm text-foreground">{children}</div>;
}

/**
 * The validator prompt's body, editable in place (matches the prototype's
 * Validator Prompt block). It's an `AiPrompt` (feature_key "validator") -- a
 * separate resource from `/checks` -- so a save creates a new active
 * `AiPromptVersion`, the same write path its card in "Generation Prompts" uses.
 */
function ValidatorPromptBlock() {
  const { data: aiPrompts } = useGetAiPrompts();
  const prompt = useMemo(() => aiPrompts?.find((p) => p.feature_key === "validator"), [aiPrompts]);
  const activeVersion = prompt?.versions.find((v) => v.is_active);
  const body = activeVersion?.prompt_text ?? prompt?.default_prompt ?? null;
  const { mutateAsync: createVersion } = useCreateAiPromptVersion(prompt?.id ?? 0);

  return (
    <div className="space-y-1.5">
      <h3 className="text-xl font-semibold text-foreground">Validator Prompt</h3>
      <EditableText
        readOnly={!prompt}
        rows={16}
        value={body}
        emptyText="No validator prompt configured yet."
        maxHeightClass="max-h-[28rem]"
        onSave={
          prompt
            ? (v) =>
                createVersion({
                  name: `Edited ${new Date().toLocaleString()}`,
                  prompt_text: v,
                  description: undefined,
                })
            : undefined
        }
        savedMessage="Validator prompt saved"
        ariaLabel="Validator prompt"
        testId="validator-page-prompt-body"
      />
    </div>
  );
}

/**
 * The editable consolidated Validator page: the "How validation works"
 * explainer, the validator prompt (read-only here), then every registry check
 * (including inactive/app_side ones) as a collapsible card with a master
 * enable/disable, an editable parent rule, and an Applications table -- one
 * expandable row per prompt it's scoped to, with its own severity, refinement,
 * per-scope active toggle, and Remove.
 */
export function ValidatorPage({ focusFeature = null }: { focusFeature?: string | null }) {
  const { data: checks, isLoading, isError } = useGetChecks();
  const [howOpen, setHowOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({});

  // Disabled checks sink to the bottom, otherwise registry order is preserved.
  const orderedChecks = useMemo(() => {
    if (!checks) return [];
    return checks
      .map((check, index) => ({ check, index }))
      .sort((a, b) => {
        const offA = a.check.active ? 0 : 1;
        const offB = b.check.active ? 0 : 1;
        return offA - offB || a.index - b.index;
      })
      .map(({ check }) => check);
  }, [checks]);

  // Features come from the prompts checks are actually scoped to.
  const featureOptions = useMemo(() => {
    const seen = new Map<string, string>();
    (checks ?? []).forEach((check) =>
      check.feature_configs.forEach((fc) => {
        seen.set(resolveFeatureCategoryKey(fc.feature_name), resolveFeatureCategoryLabel(fc.feature_name));
      })
    );
    return Array.from(seen, ([value, label]) => ({ value, label }));
  }, [checks]);

  const filterColumns: FilterColumn[] = useMemo(
    () => [
      { id: "feature", label: "Feature", options: featureOptions },
      {
        id: "state",
        label: "State",
        options: [
          { value: "on", label: "Active only" },
          { value: "off", label: "Turned off only" },
        ],
      },
    ],
    [featureOptions]
  );

  const featureFilter = activeFilters.feature ?? [];
  const stateFilter = activeFilters.state ?? [];
  const query = search.trim().toLowerCase();

  const visibleChecks = orderedChecks.filter((check) => {
    if (stateFilter.length === 1) {
      if (stateFilter[0] === "on" && !check.active) return false;
      if (stateFilter[0] === "off" && check.active) return false;
    }
    if (
      featureFilter.length &&
      !check.feature_configs.some((fc) => featureFilter.includes(resolveFeatureCategoryKey(fc.feature_name)))
    ) {
      return false;
    }
    if (query) {
      const haystack = [
        check.label,
        check.plain ?? "",
        check.base_rule,
        ...check.feature_configs.map((fc) => resolveFeatureCategoryLabel(fc.feature_name)),
      ]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(query)) return false;
    }
    return true;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8" data-testid="validator-page-loading">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="p-4 rounded-lg border border-dashed border-destructive/40 text-center"
        data-testid="validator-page-error"
      >
        <p className="text-sm text-muted-foreground">
          Couldn&apos;t load the validator checks. Try refreshing the page.
        </p>
      </div>
    );
  }

  if (!checks || checks.length === 0) {
    return (
      <div
        className="p-4 rounded-lg border border-dashed border-border text-center"
        data-testid="validator-page-empty"
      >
        <p className="text-sm text-muted-foreground">No validator checks configured yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 min-w-0">
      <div className="space-y-2">
        <ExpandCollapseToggle
          expanded={howOpen}
          onToggle={() => setHowOpen((open) => !open)}
          expandLabel="How validation works"
          collapseLabel="Hide how validation works"
          className="w-auto justify-start text-sm"
        />

        {howOpen && (
          <div className="space-y-6 max-w-4xl pt-2">
            <div className="space-y-3">
              <h4 className="text-lg font-semibold text-foreground">Severity</h4>
              <p className="text-sm text-foreground">
                Every check carries a severity per prompt, which decides what happens next when
                something gets flagged.
              </p>
              <DefinitionBlock>
                <p>
                  <span className="font-semibold text-status-error-strong">Hard fail</span> — auto-retries
                  using corrective guidance, up to <Token>max_attempts</Token>, invisibly to the
                  user until either a clean result comes back or attempts run out. If every attempt
                  fails, the last corrective alternative is surfaced directly, never a generic
                  error. Use this for anything that would be wrong or broken if it shipped.
                </p>
                <p>
                  <span className="font-semibold text-status-warning-strong">Warning</span> — never blocks
                  or retries. A small inline badge sits next to the output, reason available on
                  hover. Use this for something worth a human's attention but not wrong enough to
                  hold up the result.
                </p>
                <p>
                  <span className="font-semibold text-foreground">
                    Silent retry only covers corrections that don't change meaning
                  </span>{" "}
                  — trimming length, fixing formatting, adjusting phrasing. The moment a retry
                  alters or drops something the user explicitly specified, that correction must be
                  disclosed in the final result rather than folded silently into clean-looking
                  output.
                </p>
              </DefinitionBlock>
            </div>

            <div className="space-y-3">
              <h4 className="text-lg font-semibold text-foreground">Applications</h4>
              <p className="text-sm text-foreground">
                A check is applied per generation prompt, not per feature. Each application carries
                its own severity and its <Token>{"{{feature_name}}"}</Token>, and may optionally add
                a refinement.
              </p>
              <DefinitionBlock>
                <p>
                  <span className="font-semibold text-foreground">Refinements are optional:</span>{" "}
                  an application with no refinement enforces the check's parent rule as written. When
                  a refinement is added it's appended directly to that parent rule and compiled into{" "}
                  <Token>{"{{ruleset}}"}</Token> — the text that actually gets sent to the model.
                  It's a live instruction, not internal documentation, and it's what lets one shared
                  check enforce a prompt-specific rule without needing a new check for every nuance.
                </p>
              </DefinitionBlock>
            </div>
          </div>
        )}
      </div>

      <ValidatorPromptBlock />

      <Separator />

      <div className="space-y-4">
        <div>
          <h3 className="text-xl font-semibold text-foreground">Checks</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Each check has one parent rule, plus an application for every prompt it runs on — each
            with its own severity and an optional refinement.
          </p>
        </div>

        <SearchFilterBar
          search={search}
          onSearchChange={setSearch}
          placeholder="Search checks…"
          filterColumns={filterColumns}
          activeFilters={activeFilters}
          onFiltersChange={setActiveFilters}
        />

        {visibleChecks.length === 0 && (
          <p className="text-sm text-muted-foreground">No checks match your search.</p>
        )}

        <div className="space-y-3">
          {visibleChecks.map((check) => (
            <CheckCard key={check.id} check={check} focusFeature={focusFeature} />
          ))}
        </div>
      </div>
    </div>
  );
}

function CheckCard({ check, focusFeature = null }: { check: RegistryCheck; focusFeature?: string | null }) {
  const updateCheck = useUpdateCheck(check.id);
  const [isOpen, setIsOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const isOff = !check.active;
  const open = isOpen && !isOff;

  // AUT-394: when arriving via a prompt's "Manage in Validator →", auto-expand any check
  // block that scopes the source prompt so its row is visible to highlight.
  const isFocusTarget =
    !!focusFeature && !isOff && check.feature_configs.some((fc) => fc.feature_name === focusFeature);
  useEffect(() => {
    if (isFocusTarget) setIsOpen(true);
  }, [isFocusTarget, focusFeature]);

  return (
    <div
      className={cn(
        "rounded-lg border p-4 space-y-3 transition-colors",
        isOff ? "border-dashed bg-muted/40" : "bg-card border-border"
      )}
      data-testid={`validator-page-check-${check.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          aria-expanded={open}
          disabled={isOff}
          className={cn(
            "flex items-start gap-2 min-w-0 flex-1 text-left transition-opacity",
            isOff && "opacity-60 cursor-default"
          )}
        >
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-lg font-semibold text-foreground">{check.label}</h4>
              {isOff && (
                <Badge variant="outline" className="text-xs">
                  Off
                </Badge>
              )}
              <Badge variant="secondary" className="text-xs">
                {check.feature_configs.length} application{check.feature_configs.length === 1 ? "" : "s"}
              </Badge>
              {check.app_side && (
                <Badge
                  variant="secondary"
                  className="text-xs"
                  data-testid={`validator-page-check-app-side-${check.id}`}
                >
                  Application-side
                </Badge>
              )}
              {!isOff &&
                (open ? (
                  <ChevronUp className="h-4 w-4 shrink-0 text-primary" />
                ) : (
                  <ChevronDown className="h-4 w-4 shrink-0 text-primary" />
                ))}
            </div>
            {check.plain && !check.app_side && <p className="text-sm text-foreground">{check.plain}</p>}
          </div>
        </button>
        <div className="flex items-center gap-2 shrink-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" aria-label={`About ${check.label}`}>
                <Info className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              Disabling a check turns it off for every prompt it is scoped to, across every
              feature.
            </TooltipContent>
          </Tooltip>
          <Switch
            checked={check.active}
            onCheckedChange={(active) => updateCheck.mutate({ active })}
            aria-label={`${check.active ? "Disable" : "Enable"} ${check.label}`}
            data-testid={`validator-page-check-active-toggle-${check.id}`}
          />
        </div>
      </div>

      {open && (
        <div className="space-y-4">
          <EditableText
            size="sm"
            rows={1}
            label={<span className="text-sm font-semibold text-foreground">Name</span>}
            value={check.label}
            onSave={(v) => {
              const trimmed = v.trim();
              return trimmed === "" ? Promise.resolve() : updateCheck.mutateAsync({ label: trimmed });
            }}
            savedMessage="Check name saved"
            ariaLabel={`${check.label} name`}
            testId={`validator-page-check-label-${check.id}`}
          />

          {/* AUT-392: only AI-validated checks carry a plain-language gloss; app-side
              (deterministic) checks have none, per the brief. */}
          {!check.app_side && (
            <EditableText
              size="sm"
              rows={2}
              label={
                <span className="text-sm">
                  <span className="font-semibold text-foreground">Description</span>{" "}
                  <span className="font-medium text-muted-foreground">— plain-language gloss, optional</span>
                </span>
              }
              value={check.plain}
              emptyText="No description."
              onSave={(v) => {
                const trimmed = v.trim();
                return updateCheck.mutateAsync({ plain: trimmed === "" ? null : v });
              }}
              savedMessage={`${check.label} description saved`}
              ariaLabel={`${check.label} description`}
              testId={`validator-page-check-plain-${check.id}`}
            />
          )}

          <EditableText
            size="sm"
            rows={5}
            label={
              <span className="text-sm">
                <span className="font-semibold text-foreground">Parent rule</span>{" "}
                <span className="font-medium text-muted-foreground">
                  — shared across every prompt using this check
                </span>
              </span>
            }
            value={check.base_rule}
            onSave={(v) => updateCheck.mutateAsync({ base_rule: v })}
            savedMessage={`${check.label} rule saved`}
            ariaLabel={`${check.label} parent rule`}
            testId={`validator-page-check-base-rule-${check.id}`}
          />

          <div className="space-y-4">
            {check.feature_configs.length > 0 && (
              <Table data-testid={`validator-page-applications-table-${check.id}`}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-sm">Application</TableHead>
                    <TableHead className="text-sm">Severity</TableHead>
                    <TableHead className="text-sm">Refinement</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {check.feature_configs.map((fc) => (
                    <ApplicationRow key={fc.feature_name} check={check} config={fc} focusFeature={focusFeature} />
                  ))}
                </TableBody>
              </Table>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsAddModalOpen(true)}
              data-testid={`validator-page-add-application-${check.id}`}
            >
              <Plus className="h-4 w-4" />
              Apply this check to another prompt
            </Button>
          </div>
        </div>
      )}

      {isAddModalOpen && <AddCheckToPromptModal open onOpenChange={setIsAddModalOpen} check={check} />}
    </div>
  );
}

type ApplicationRowProps = {
  check: RegistryCheck;
  config: RegistryFeatureConfig;
  focusFeature?: string | null;
};

function ApplicationRow({ check, config, focusFeature = null }: ApplicationRowProps) {
  const updateConfig = useUpdateFeatureCheckConfig(config.feature_name, check.id);
  const deleteConfig = useDeleteFeatureCheckConfig(config.feature_name, check.id);
  const { data: aiPrompts } = useGetAiPrompts();

  const [isOpen, setIsOpen] = useState(false);
  const rowRef = useRef<HTMLTableRowElement>(null);
  const [highlighted, setHighlighted] = useState(false);

  // AUT-394: this is the row a prompt's "Manage in Validator →" cross-link points at.
  // Expand it, scroll it into view, and flash a highlight that fades after a moment.
  const isFocused = !!focusFeature && config.feature_name === focusFeature;
  useEffect(() => {
    if (!isFocused) return;
    setIsOpen(true);
    setHighlighted(true);
    rowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    const timer = setTimeout(() => setHighlighted(false), 2500);
    return () => clearTimeout(timer);
  }, [isFocused]);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirmingRemove, setIsConfirmingRemove] = useState(false);
  const [refinementDraft, setRefinementDraft] = useState(config.refinement ?? "");
  const [severityDraft, setSeverityDraft] = useState<Severity>(config.severity);

  useEffect(() => {
    if (!isEditing) {
      setRefinementDraft(config.refinement ?? "");
      setSeverityDraft(config.severity);
    }
  }, [config.refinement, config.severity, isEditing]);

  const featureLabel = resolveFeatureCategoryLabel(config.feature_name);
  const promptLabel = aiPrompts?.find((p) => p.feature_key === config.feature_name)?.label ?? config.feature_name;

  const startEdit = () => {
    setRefinementDraft(config.refinement ?? "");
    setSeverityDraft(config.severity);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setRefinementDraft(config.refinement ?? "");
    setSeverityDraft(config.severity);
    setIsEditing(false);
  };

  const save = async () => {
    setIsSaving(true);
    try {
      const trimmed = refinementDraft.trim();
      await updateConfig.mutateAsync({
        refinement: trimmed === "" ? null : refinementDraft,
        severity: severityDraft,
      });
      setIsEditing(false);
    } catch {
      // Stays in edit mode -- the mutation's onError already surfaced the error.
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <TableRow
        ref={rowRef}
        className={cn(
          "[&>*]:text-sm cursor-pointer transition-colors duration-500",
          highlighted && "bg-primary/10"
        )}
        data-focused={isFocused ? "true" : undefined}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        data-testid={`validator-page-application-${check.id}-${config.feature_name}`}
      >
        <TableCell className="text-foreground">
          <span className="flex items-center gap-1.5">
            {isOpen ? (
              <ChevronUp className="h-4 w-4 shrink-0 text-primary" />
            ) : (
              <ChevronDown className="h-4 w-4 shrink-0 text-primary" />
            )}
            <span>
              <span className="font-normal">{featureLabel} / </span>
              <span className="font-semibold">{promptLabel}</span>
            </span>
            {!config.active && (
              <Badge
                variant="outline"
                className="text-xs"
                data-testid={`validator-page-application-disabled-${check.id}-${config.feature_name}`}
              >
                Disabled
              </Badge>
            )}
          </span>
        </TableCell>
        <TableCell>
          <Badge variant={config.severity === "hard_fail" ? "error" : "warning"} className="text-xs">
            {SEVERITY_LABELS[config.severity]}
          </Badge>
        </TableCell>
        <TableCell className="text-muted-foreground">
          {config.refinement ? (
            "Custom"
          ) : (
            <span aria-label="No refinement — uses the rule as written">—</span>
          )}
        </TableCell>
      </TableRow>

      {isOpen && (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={3} className="py-0">
            <div className="ml-6 border-l-2 border-primary pl-4 pt-4 pb-4 space-y-3">
              <EditableText
                editing={isEditing}
                readOnly={!isEditing}
                size="sm"
                rows={4}
                emptyText="Nothing added yet — the base rule alone applies."
                label={
                  <span className="text-sm">
                    <span className="font-semibold text-foreground">Refinement</span>{" "}
                    <span className="font-medium text-muted-foreground">
                      — this prompt only, optional
                    </span>
                  </span>
                }
                value={isEditing ? refinementDraft : config.refinement}
                onChange={setRefinementDraft}
                ariaLabel={`${featureLabel} refinement for ${check.label}`}
                testId={`validator-page-refinement-${check.id}-${config.feature_name}`}
              />

              {isEditing && (
                <div className="space-y-1.5">
                  <p className="text-sm font-semibold text-foreground">Severity</p>
                  <SeverityToggle value={severityDraft} onChange={setSeverityDraft} promptLabel={promptLabel} />
                </div>
              )}

              <div className="flex items-center justify-between gap-2 pt-1 border-t border-border/40">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={config.active}
                    onCheckedChange={(active) => updateConfig.mutate({ active })}
                    aria-label={`${config.active ? "Disable" : "Enable"} ${check.label} for ${featureLabel}`}
                    data-testid={`validator-page-application-active-toggle-${check.id}-${config.feature_name}`}
                  />
                  <span className="text-sm text-muted-foreground">Enabled for this prompt</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                    aria-label={`Remove ${check.label} from ${featureLabel}`}
                    onClick={() => setIsConfirmingRemove(true)}
                    data-testid={`validator-page-remove-${check.id}-${config.feature_name}`}
                  >
                    <X className="h-4 w-4" />
                    Remove
                  </Button>

                  {isEditing ? (
                    <>
                      <Button variant="outline" size="sm" onClick={cancelEdit} disabled={isSaving}>
                        Cancel
                      </Button>
                      <Button size="sm" onClick={save} disabled={isSaving}>
                        {isSaving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                        Save
                      </Button>
                    </>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={startEdit}>
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </Button>
                  )}
                </div>
              </div>

              <ConfirmDeleteDialog
                open={isConfirmingRemove}
                onOpenChange={setIsConfirmingRemove}
                title="Remove this application?"
                description={
                  <>
                    {check.label} will no longer run on{" "}
                    <span className="font-medium text-foreground">
                      {featureLabel} / {promptLabel}
                    </span>
                    . Its refinement will be discarded.
                  </>
                }
                confirmLabel="Remove application"
                isPending={deleteConfig.isPending}
                onConfirm={() =>
                  deleteConfig.mutate(undefined, {
                    onSuccess: () => setIsConfirmingRemove(false),
                  })
                }
              />
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
